import React, { useRef, useEffect } from 'react';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import * as THREE from 'three';
import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer';
// import { themeStore } // Use appStore.theme instead from '../../stores/ThemeStore';
import { observer } from 'mobx-react-lite';
import './CurlNoiseBackground.css';

// Vertex shader for particles
const vertexShader = `
uniform sampler2D posMap;
uniform sampler2D infoMap;
varying float posAngle;
varying vec4 info;

  void main() {
    info = texture2D(infoMap, uv);
    vec4 pos = texture2D(posMap, uv);
    posAngle = atan(pos.y, pos.x);

    // modelMatrix transforms the coordinates local to the model into world space
    vec4 worldPos = modelMatrix * pos;
    // viewMatrix transform the world coordinates into the world space viewed by the camera (view space)
    vec4 mvPosition = viewMatrix * worldPos;

    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = info.w;
}
`;

// Fragment shader for particle appearance
const fragmentShader = `
#define TWO_PI 6.28318530718
#define PI 3.1415926538
uniform vec2 u_resolution;
varying float posAngle;
uniform float u_time;
uniform float u_isLightTheme;
varying vec4 info;

// https://www.shadertoy.com/view/ll2GD3
vec3 pal(in float t, in vec3 a, in vec3 b, in vec3 c, in vec3 d) {
    return a + b*cos(6.28318*(c*t+d));
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;

  // -PI < fadeAngle < PI
  float fadeAngle = mod(u_time * -2.5, TWO_PI) - PI;
  // -PI < posAngle * 2. < PI
  float angleDiff = abs(fadeAngle - posAngle * 2.);

  // Enhanced glow effect
  float glow = 0.7 - length(gl_PointCoord - vec2(0.5)) * 2.0;
  glow = max(0.0, glow);
  glow = pow(glow, 1.5);
  
  vec3 color;
  
  if (u_isLightTheme > 0.5) {
    // Much darker color palette for light theme
    color = pal(info.x * 10., vec3(0.1,0.1,0.3), vec3(0.2,0.2,0.2), vec3(1.0,1.0,1.0), vec3(0.0,0.2,0.3));
  } else {
    // Original color palette for dark theme
    color = pal(info.x * 10., vec3(0.5,0.5,0.5), vec3(0.5,0.5,0.5), vec3(1.0,1.0,1.0), vec3(0.0,0.10,0.20));
  }
  
  // Apply glow effect
  gl_FragColor = vec4(color * glow, glow * 0.8);
}
`;

// Curl noise implementation with perlin noise
const curlNoiseShader = `
//
// GLSL textureless classic 3D noise "cnoise",
// with an RSL-style periodic variant "pnoise".
// Author:  Stefan Gustavson (stefan.gustavson@liu.se)
// Version: 2011-10-11
//
// Many thanks to Ian McEwan of Ashima Arts for the
// ideas for permutation and gradient selection.
//
// Copyright (c) 2011 Stefan Gustavson. All rights reserved.
// Distributed under the MIT license. See LICENSE file.
// https://github.com/stegu/webgl-noise
//

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
  return mod289(((x*34.0)+10.0)*x);
}

vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec3 fade(vec3 t) {
  return t*t*t*(t*(t*6.0-15.0)+10.0);
}

// Classic Perlin noise
float cnoise(vec3 P) {
  vec3 Pi0 = floor(P); // Integer part for indexing
  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
  Pi0 = mod289(Pi0);
  Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
  return 2.2 * n_xyz;
}

// Curl noise calculation
vec2 curl_noise(vec3 p) {
    const float dt = 1e-4;
    vec2 ds = vec2(dt, 0.0);
    
    float n0 = cnoise(p);
    float n1 = cnoise(p + vec3(ds.xy, 0.));
    float n2 = cnoise(p + vec3(ds.yx, 0.));
    
    vec2 grad = vec2(n1 - n0, n2 - n0) / ds.x;
    vec2 curl = vec2(grad.y, -grad.x);

    return curl;
}
`;

// Simulation fragment shader
const simulationShader = `
#define PI 3.1415926538

uniform sampler2D infomap;
uniform float u_time;
uniform float radius;
uniform float circularForceFactor;
uniform float curlPatternScale;
uniform float curlVaryingSpeed;
uniform float curlForceFactor;

${curlNoiseShader}

void main() {
    // The size of the computation is defined as 'resolution' automatically in the shader
    vec2 cellSize = 1.0 / resolution.xy;

    // Get current UV coordinates
    vec2 uv = gl_FragCoord.xy * cellSize;
    vec4 pos = texture2D(posmap, uv);
    vec4 info = texture2D(infomap, uv);

    // info.x = speed of the particles
    float targetAngle = atan(pos.y, pos.x) + info.x;
    // introducing radius fluctuations via cosine and info.z
    float targetRadius = radius + (cos(targetAngle * 2. + 2.) + 1.) * info.z;

    // Using curl that returns 2D force field from 3D perlin noise
    // Feed time to z component for the perlin noise
    vec2 curled = vec2(0.);
    // Apply different curl patterns based on particle size
    if (info.w > 1.0) {
        curled = curl_noise(vec3(pos.xy * curlPatternScale, u_time * curlVaryingSpeed)) * curlForceFactor;
    } else {
        curled = curl_noise(vec3(pos.xy * curlPatternScale, -1. * u_time * curlVaryingSpeed)) * curlForceFactor;
    }
    
    vec3 targetPos = vec3(cos(targetAngle), sin(targetAngle), 0.0) * targetRadius;
    // new position = curl force + an eased/dampened force towards the target position
    pos.xy += curled.xy + (targetPos.xy - pos.xy) * circularForceFactor;

    gl_FragColor = pos;
  }
`;

const CurlNoiseBackground: React.FC = observer(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = themeStore;
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Texture dimensions for simulation - increased for more particles
    const FBO_WIDTH = 800;
    const FBO_HEIGHT = 800;
    // Control FPS of the GPGPU renderer (animation speed)
    const FPS_INTERVAL = 1/60;
    
    // Parameters for the simulation
    const params = {
      radius: 0.8
    };
    
    // Set I - Calmer curl field with flying colors
    const curlParams = {
      a: 0.13,  // circularForceFactor
      b: 5.0,   // curlPatternScale
      c: 0.4,   // curlVaryingSpeed
      d: 0.0075 // curlForceFactor
    };
    
    // Scene setup
    const scene = new THREE.Scene();
    
    // Setup renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true 
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0); // Transparent background
    containerRef.current.appendChild(renderer.domElement);
    
    // Create camera
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 1000);
    camera.position.z = 4;
    
    let delta = 0;
    let gpuCompute: GPUComputationRenderer;
    let posmapVariable: any;
    let points: THREE.Points;
    
    // Initialize GPU Computation
    const initGPGPU = () => {
      // Creates the gpu computation class and sets it up
      gpuCompute = new GPUComputationRenderer(FBO_WIDTH, FBO_HEIGHT, renderer);
      if (renderer.capabilities.isWebGL2 === false) {
        gpuCompute.setDataType(THREE.HalfFloatType);
      }
      
      // Create position and info textures
      const posmap = gpuCompute.createTexture();
      const infomap = gpuCompute.createTexture();
      fillPositionTexture(posmap);
      fillInfoTexture(infomap);
      
      // Add simulation variable
      posmapVariable = gpuCompute.addVariable('posmap', simulationShader, posmap);
      gpuCompute.setVariableDependencies(posmapVariable, [posmapVariable]);
      
      // Set uniforms for the simulation
      posmapVariable.material.uniforms['infomap'] = { value: infomap };
      posmapVariable.material.uniforms['u_time'] = { value: 0.0 };
      posmapVariable.material.uniforms['radius'] = { value: params.radius };
      posmapVariable.material.uniforms['circularForceFactor'] = { value: curlParams.a };
      posmapVariable.material.uniforms['curlPatternScale'] = { value: curlParams.b };
      posmapVariable.material.uniforms['curlVaryingSpeed'] = { value: curlParams.c };
      posmapVariable.material.uniforms['curlForceFactor'] = { value: curlParams.d };
      
      // Initialize the computation
      const error = gpuCompute.init();
      if (error !== null) {
        console.error('GPGPU init error:', error);
      }
      
      // Create particle system
      const count = FBO_WIDTH * FBO_HEIGHT;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const uvs = new Float32Array(count * 2);
      
      for (let i = 0; i < FBO_WIDTH; i++) {
        for (let j = 0; j < FBO_HEIGHT; j++) {
          const index = (i + j * FBO_WIDTH);
          
          positions[index * 3 + 0] = Math.random() - 0.5;
          positions[index * 3 + 1] = Math.random() - 0.5;
          positions[index * 3 + 2] = 0;
          uvs[index * 2 + 0] = i / FBO_WIDTH;
          uvs[index * 2 + 1] = j / FBO_HEIGHT;
        }
      }
      
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
      
      // Create material
      const material = new THREE.ShaderMaterial({
        uniforms: {
          u_time: { value: 0.0 },
          u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
          u_isLightTheme: { value: theme === 'light' ? 1.0 : 0.0 },
          posMap: { value: null },
          infoMap: { value: infomap },
        },
        transparent: true,
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      });
      
      // Create points and add to scene
      points = new THREE.Points(geometry, material);
      scene.add(points);
    };
    
    // Fill textures with initial data
    const fillPositionTexture = (texture: THREE.DataTexture) => {
      const pixels = texture.image.data;
      let p = 0;
      
      for (let j = 0; j < FBO_HEIGHT; j++) {
        for (let i = 0; i < FBO_WIDTH; i++) {
          let theta = Math.random() * Math.PI * 2;
          let r = params.radius;
          
          pixels[p + 0] = r * Math.cos(theta);  // x
          pixels[p + 1] = r * Math.sin(theta);  // y
          pixels[p + 2] = 1.0;                  // z
          pixels[p + 3] = 1.0;                  // w
          
          p += 4;
        }
      }
    };
    
    const fillInfoTexture = (texture: THREE.DataTexture) => {
      const pixels = texture.image.data;
      let p = 0;
      
      for (let j = 0; j < FBO_HEIGHT; j++) {
        for (let i = 0; i < FBO_WIDTH; i++) {
          // speeds, scatter them in opposite directions
          pixels[p + 0] = (0.01 + 0.005 * Math.random()) * (Math.random() < 0.8 ? -1 : 1) * 10.0;
          // spare randomness not in use
          pixels[p + 1] = Math.random();
          // radii fluctuation
          pixels[p + 2] = 0.08 * Math.random() - 0.04;
          // particle size - decrease size to accommodate more particles
          pixels[p + 3] = 1.0 + Math.random() * 1.5;
          
          p += 4;
        }
      }
    };
    
    // Initialize
    initGPGPU();
    
    // Animation loop
    let animationFrameId: number;
    const animate = (time: number) => {
      time *= 0.001; // Convert to seconds
      posmapVariable.material.uniforms['u_time'].value = time;
      (points.material as THREE.ShaderMaterial).uniforms['u_time'].value = time;
      (points.material as THREE.ShaderMaterial).uniforms['u_isLightTheme'].value = theme === 'light' ? 1.0 : 0.0;
      
      // Control the fps of the gpgpu renderer
      delta += 1/60;
      if (delta > FPS_INTERVAL) {
        // Do the gpu computation
        gpuCompute.compute();
        delta = delta % FPS_INTERVAL;
      }
      
      // Update points with computed position data
      (points.material as THREE.ShaderMaterial).uniforms['posMap'].value = 
        gpuCompute.getCurrentRenderTarget(posmapVariable).texture;
      
      // Render the scene
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Start animation
    animate(0);
    
    // Handle window resize
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
      (points.material as THREE.ShaderMaterial).uniforms.u_resolution.value.set(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      
      points.geometry.dispose();
      (points.material as THREE.ShaderMaterial).dispose();
      renderer.dispose();
      gpuCompute.dispose();
    };
  }, [theme]);
  
  return <div ref={containerRef} className="curl-noise-background" />;
});

export default CurlNoiseBackground; 